import { Controller } from '@hotwired/stimulus';

// HTTP submissions can finish before their queued Turbo updates arrive. The
// optional completion headers name a rendered element and its monotonic version.
export default class extends Controller {
  static targets = ['panel', 'spinner', 'message', 'reload'];
  static values = { timeout: { type: Number, default: 10000 } };

  connect() {
    this.pending = new Map();
    this.panel = this.panelTarget;
    this.spinner = this.spinnerTarget;
    this.message = this.messageTarget;
    this.reloadButton = this.reloadTarget;
    this.observer = new MutationObserver(() => this.check());
  }

  disconnect() {
    this.reset();
  }

  start({ detail: { formSubmission } }) {
    const form = formSubmission.formElement;
    this.pending.set(formSubmission, {
      group: form.dataset.submissionGroup,
      dialog: form.closest('dialog'),
      label: form.dataset.submissionLabel || (form.method.toLowerCase() === 'get' ? 'Loading…' : 'Saving…')
    });
    this.show();
  }

  finish({ detail: { formSubmission, success, fetchResponse } }) {
    const entry = this.pending.get(formSubmission);
    if (!entry) return;

    const headers = fetchResponse?.response.headers;
    const target = headers?.get('X-Submission-Target');
    const versionHeader = headers?.get('X-Submission-Version');
    const version = Number(versionHeader);
    if (success && target && versionHeader && Number.isFinite(version) && document.getElementById(target)) {
      entry.completion = { target, version };
      entry.timeout = setTimeout(() => {
        entry.delayed = true;
        this.show();
      }, this.timeoutValue);
      this.observer.observe(this.element, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-submission-version'] });
      this.check();
    } else {
      this.remove(formSubmission);
      this.show();
    }
  }

  check() {
    let changed = false;
    for (const [submission, entry] of this.pending) {
      if (!entry.completion) continue;
      const target = document.getElementById(entry.completion.target);
      // Navigating away from the target also ends its waiting indicator.
      if (!target || Number(target.dataset.submissionVersion) >= entry.completion.version) {
        this.remove(submission);
        changed = true;
      }
    }
    if (![...this.pending.values()].some(entry => entry.completion)) this.observer.disconnect();
    if (changed) this.show();
    else this.lockGroups();
  }

  lockGroups() {
    const groups = new Set([...this.pending.values()].map(entry => entry.group).filter(Boolean));
    for (const id of new Set([...(this.lockedGroups || []), ...groups])) {
      const group = document.getElementById(id);
      if (group) group.inert = groups.has(id);
    }
    this.lockedGroups = groups;
  }

  show() {
    this.lockGroups();
    const entries = [...this.pending.values()];
    const entry = entries.findLast(item => !item.delayed) || entries.at(-1);
    this.panel.hidden = !entry;
    this.spinner.hidden = !!entry?.delayed;
    this.reloadButton.hidden = !entry?.delayed;
    this.message.textContent = entry?.delayed ? 'The update is delayed. Refresh to see the latest state.' : entry?.label || '';
    this.place();
  }

  closing() {
    // The cancelable closing event precedes the modal's closing marker.
    queueMicrotask(() => { if (this.element.isConnected) this.place(); });
  }

  place() {
    // Native dialogs sit above the page's stacking contexts. Keep feedback in
    // their top layer while open, without taking over the modal's lifecycle.
    const entry = [...this.pending.values()].at(-1);
    const dialog = entry?.dialog;
    const host = dialog?.isConnected && dialog.open && !dialog.hasAttribute('data-closing') ? dialog : this.element;
    if (this.panel.parentElement !== host) host.append(this.panel);
  }

  remove(submission) {
    clearTimeout(this.pending.get(submission)?.timeout);
    this.pending.delete(submission);
  }

  reset() {
    for (const submission of this.pending.keys()) this.remove(submission);
    this.observer.disconnect();
    this.show();
  }

  reload() {
    window.location.reload();
  }
}
